<template>
  <div style="width: 100%">
    <v-row
      v-if="
        role('manager', 'departmentManager', 'admin', 'safetyRepresentative')
      "
      no-gutters
      align="center"
      class="pl-4 mt-1 mb-2"
    >
      <v-col>
        <div :class="titleClass">
          {{ employee | nameId }}
        </div>
      </v-col>
      <v-col v-if="role('admin', 'safetyRepresentative')" cols="auto">
        <v-btn color="primary" :to="{ name: 'mentorship-ongoing' }">
          {{ $t("mentorship.allActive") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row align="center" no-gutters class="mt-1 mb-3 pl-4">
      <v-col>
        <div :class="titleClass">
          <span v-if="activeView === 'mentorships'">
            {{
              id == me.id
                ? $t("mentorship.subheader.activeMentorshipMe")
                : $t("mentorship.subheader.activeMentorship")
            }}
          </span>
          <span v-else-if="activeView === 'actions'">
            {{
              id == me.id
                ? $t("mentorship.subheader.activeMentorshipActionsMe")
                : $t("mentorship.subheader.activeMentorshipActions")
            }}
          </span>
          <span v-else-if="activeView === 'unconfirmed'">
            {{ $t("mentorship.subheader.unconfirmedMentorships") }}
          </span>
        </div>
      </v-col>

      <v-col cols="auto">
        <v-btn
          class="mr-2"
          color="primary"
          :text="activeView !== 'mentorships'"
          @click="setActive('mentorships')"
        >
          {{ $t("mentorship.button") }}
        </v-btn>

        <v-btn
          color="primary"
          :text="activeView !== 'actions'"
          @click="setActive('actions')"
        >
          {{ $t("mentorship.actions.button") }}
        </v-btn>

        <v-btn
          v-if="
            role(
              'manager',
              'departmentManager',
              'admin',
              'safetyRepresentative',
            )
          "
          class="ml-2"
          color="primary"
          :text="activeView !== 'unconfirmed'"
          @click="setActive('unconfirmed')"
        >
          {{ $t("mentorship.unconfirmed") }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Active mentorships -->
    <template v-if="activeView === 'mentorships'">
      <employee-mentorship
        :id="id"
        :mentorships="employee.activeMentorships"
        :mentorView="true"
        @refresh="refreshCurrentQuery"
        :noDataText="
          me.id == id
            ? $t('mentorship.subheader.noActiveMentorshipsForMe')
            : $t('mentorship.subheader.noActiveMentorshipsForEmployee')
        "
        :loading="$apollo.loading"
      />
    </template>

    <!-- Active mentorship learning actions-->
    <template v-else-if="activeView === 'actions'">
      <employee-active-mentorship-actions-mentoring
        :activeMentorshipActions="employee.activeMentorshipActions || []"
        :id="+id"
        @refresh="refreshCurrentQuery"
        :noDataText="
          me.id == id
            ? $t('mentorship.subheader.noActiveMentorshipActionsForMe')
            : $t('mentorship.subheader.noActiveMentorshipActionsForEmployee')
        "
        :loading="$apollo.loading"
      />
    </template>

    <!-- Unconfirmed mentorships -->
    <template v-else-if="activeView === 'unconfirmed'">
      <employee-mentorship
        :id="id"
        :mentorships="employee.unconfirmedMentorships"
        :mentorView="true"
        :showMentorColumn="true"
        :sortByCategoryLocation="true"
        @refresh="refreshCurrentQuery"
        :noDataText="$t('mentorship.subheader.noUnconfirmedMentorshipsForMe')"
        :loading="$apollo.loading"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import EmployeeActiveMentorshipsQuery from "@/graphql/queries/EmployeeActiveMentorships.gql";
import EmployeeActiveMentorshipActionsQuery from "@/graphql/queries/EmployeeActiveMentorshipActions.gql";
import EmployeeUnconfirmedMentorshipsQuery from "@/graphql/queries/EmployeeUnconfirmedMentorships.gql";

import EmployeeActiveMentorshipActionsMentoring from "@/components/employee/EmployeeActiveMentorshipActionsMentoring.vue";
import EmployeeMentorship from "@/components/EmployeeMentorship";

export default {
  props: {
    id: {
      type: [Number, String],
      validator: function(value) {
        if (typeof value === "number") {
          return true;
        } else if (typeof value === "string") {
          return !isNaN(Number(value));
        }
        return false;
      },
      required: true,
    },
  },

  components: {
    EmployeeActiveMentorshipActionsMentoring,
    EmployeeMentorship,
  },
  data() {
    return {
      employee: {},
      titleClass: "title text--secondary text-uppercase",
      activeView: this.routeView || "mentorships",
    };
  },
  computed: {
    ...mapGetters(["role"]),
    ...mapState(["me"]),
    routeView() {
      const v = this.$route.query.view;
      return ["mentorships", "actions", "unconfirmed"].includes(v) ? v : null;
    },
  },
  watch: {
    routeView: {
      immediate: true,
      handler(view) {
        if (view && this.activeView !== view) {
          this.activeView = view;
        }
      },
    },
  },
  methods: {
    setActive(view) {
      if (this.activeView === view) return;

      this.$router.replace({
        query: {
          ...this.$route.query,
          view,
        },
      });
    },
    refreshCurrentQuery() {
      if (!this.$apollo.queries.employee) return;
      this.$apollo.queries.employee.refetch();
    },
  },
  apollo: {
    employee: {
      query() {
        switch (this.activeView) {
          case "mentorships":
            return EmployeeActiveMentorshipsQuery;
          case "actions":
            return EmployeeActiveMentorshipActionsQuery;
          case "unconfirmed":
            return EmployeeUnconfirmedMentorshipsQuery;
        }
      },
      variables() {
        return { id: this.id };
      },
      update(data) {
        return data.employee;
      },
    },
  },
};
</script>
